import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  Typography,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import { TicketFieldConflict } from '../../../../../services/TicketService';
import { displayFieldValue, humanizeFieldPath } from './ticketConflictUtils';

export type FieldChoice = 'mine' | 'theirs';

export interface ConflictState {
  conflicts: TicketFieldConflict[];
  choices: Record<string, FieldChoice>;
}

interface ConflictResolutionDialogProps {
  conflict: ConflictState;
  onChoiceChange: (path: string, choice: FieldChoice) => void;
  onCancel: () => void;
  onConfirm: () => void;
  isResolving: boolean;
}

// A concurrent save changed the same field(s) the user did — spec 010 User Story 3. Extracted out
// of SaveTicketButton so that file's own cyclomatic complexity stays within the Code Health gate.
export const ConflictResolutionDialog: React.FC<ConflictResolutionDialogProps> = ({
  conflict,
  onChoiceChange,
  onCancel,
  onConfirm,
  isResolving,
}) => (
  <Dialog open maxWidth="sm" fullWidth onClose={onCancel}>
    <DialogTitle sx={{ borderBottom: '2px solid #3b82f6', pb: 1.5 }}>
      <Typography component="div" variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
        Resolve Conflicting Changes
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
        Someone else changed this ticket while you were editing it. Choose which value to keep
        for each field below.
      </Typography>
    </DialogTitle>
    <DialogContent sx={{ pt: 3 }}>
      <Stack spacing={3}>
        {conflict.conflicts.map(fieldConflict => (
          <Box key={fieldConflict.path}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {humanizeFieldPath(fieldConflict.path)}
            </Typography>
            <RadioGroup
              value={conflict.choices[fieldConflict.path] ?? 'mine'}
              onChange={(e) => onChoiceChange(fieldConflict.path, e.target.value as FieldChoice)}
            >
              <FormControlLabel
                value="mine"
                control={<Radio size="small" />}
                label={`Keep mine: "${displayFieldValue(fieldConflict.incomingValue)}"`}
              />
              <FormControlLabel
                value="theirs"
                control={<Radio size="small" />}
                label={`Keep theirs: "${displayFieldValue(fieldConflict.currentValue)}"`}
              />
            </RadioGroup>
          </Box>
        ))}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onCancel} disabled={isResolving}>Cancel</Button>
      <Button
        variant="contained"
        onClick={onConfirm}
        disabled={isResolving}
        sx={{ backgroundColor: '#162346', '&:hover': { backgroundColor: '#1a2a52' }, fontWeight: 600 }}
      >
        {isResolving ? <CircularProgress size={20} /> : 'Save Resolution'}
      </Button>
    </DialogActions>
  </Dialog>
);
